import { Modal as IdsModal, ModalContent } from "@ids/react-modal";
import { ButtonVariant } from "@ids/web-component-button";
import classNames from "classnames";
import { type ReactNode, useCallback, useMemo } from "react";

import { useContentValue } from "@/hooks/api/content/useContentValue";
import { useMobileWidth } from "@/hooks/useMobileScreen";
import { matchType } from "@/utils/matchType";

import { Button } from "../button";
import { ModalVariant } from "./types";

type ModalProps = {
  open: boolean;
  close: () => void;
  content?: ReactNode | string;
  label?: string | React.ReactNode;
  size?: ModalVariant;
};

export const Modal = ({
  open,
  close,
  content,
  size = ModalVariant.MEDIUM,
  label,
}: ModalProps) => {
  const { isMobileScreen, mobileBreakpoint, isCalculating } = useMobileWidth();
  const width = useMemo(() => {
    const width = matchType(size, {
      small: "20rem",
      medium: "32rem",
      large: "48rem",
      "": "32rem",
    });
    return width;
  }, [size]);

  const { data: globalContent, isLoading } = useContentValue("globalContent");
  const contentComponent = useCallback(() => {
    if (!content) {
      return null;
    }
    const classes =
      "if typography ui medium content flex-column gap-24 medium-padding";
    if (typeof content === "string") {
      return (
        <div
          className={classes}
          dangerouslySetInnerHTML={{ __html: content ?? "" }}
        ></div>
      );
    }
    return <div className={classes}>{content}</div>;
  }, [content])();

  const headerComponent = useCallback(() => {
    if (!label) {
      return null;
    }
    const classes = "if typography heading small";
    if (typeof label === "string") {
      return (
        <h2
          className={classes}
          dangerouslySetInnerHTML={{ __html: label ?? "" }}
        ></h2>
      );
    }
    return <h2 className={classes}>{label}</h2>;
  }, [label])();

  const classes = classNames("if flex-column gap-16 pt-40", {
    "pb-20 px-20": !isMobileScreen,
  });

  if (isCalculating) {
    return null;
  }

  return (
    <IdsModal
      maxWidth={width}
      mdMaxWidth={width}
      fullWidth={true}
      mdBreakpoint={mobileBreakpoint}
      isOpen={open}
      onClose={close}
      contentPadding="small"
    >
      <ModalContent>
        <div className={classes}>
          {headerComponent}
          <div className="if flex-column gap-16 overflow-y-auto">
            {contentComponent}
            {isMobileScreen && (
              <Button.Tertiary
                variant={ButtonVariant.QUIET}
                onClick={close}
                className="if min-w-100p"
                aria-label="Sulje ikkuna"
                aria-hidden="true"
                loading={isLoading}
              >
                {globalContent?.closeButton?.value}
              </Button.Tertiary>
            )}
          </div>
        </div>
      </ModalContent>
    </IdsModal>
  );
};
